import { isValid, parseISO, set } from 'date-fns';

import { BillingSplitType } from '@/pages/donor-scheduling-partnership/components/partnership-form/constants';

type PartnershipItem = {
  account1?: { id: string } | null;
  account2?: { id: string } | null;
  account3?: { id: string } | null;
  account4?: { id: string } | null;
  account1Id?: string | null;
  account2Id?: string | null;
  account3Id?: string | null;
  account4Id?: string | null;
  percent1?: number | null;
  percent2?: number | null;
  percent3?: number | null;
  percent4?: number | null;
};

const getAccountId = (
  item: PartnershipItem | null | undefined,
  position: number,
): string | null => {
  if (item == null) return null;
  const account = item[`account${position}` as keyof PartnershipItem] as
    | { id: string }
    | null
    | undefined;
  const idKey = `account${position}Id` as keyof PartnershipItem;
  const id = item[idKey] as string | null | undefined;
  return (account?.id ?? id ?? null) || null;
};

export const determineBillingSplitType = (
  partnershipItem: PartnershipItem | null | undefined,
): BillingSplitType | null => {
  if (!partnershipItem) return null;

  const accountIds = new Set<string>();
  const accountCounts = new Map<string, number>();
  // for legacy data where percentages were stored per-account (ex: 50% for account1)
  const accountPercentSums = new Map<string, number>();

  [1, 2, 3, 4].forEach((position) => {
    const id = getAccountId(partnershipItem, position);
    const pct =
      (partnershipItem[`percent${position}` as keyof PartnershipItem] as
        | number
        | null) ?? 0;
    if (id) {
      accountIds.add(id);
      accountCounts.set(id, (accountCounts.get(id) || 0) + 1);
      accountPercentSums.set(id, (accountPercentSums.get(id) || 0) + pct);
    }
  });

  if (accountIds.size === 0) return null;

  const uniqueAccountCount = accountIds.size;
  const counts = Array.from(accountCounts.values());

  const sortedPercentSums = Array.from(accountPercentSums.values()).sort(
    (a, b) => b - a,
  );

  if (uniqueAccountCount === 1) {
    return sortedPercentSums[0] === 100 ? BillingSplitType.ONE_HUNDRED : null;
  }

  if (
    partnershipItem.percent1 === 33 &&
    partnershipItem.percent2 === 33 &&
    partnershipItem.percent3 === 34 &&
    (uniqueAccountCount === 3 ||
      (uniqueAccountCount === 2 &&
        Array.from(accountCounts.values()).some((c) => c === 2)))
  ) {
    return BillingSplitType.THIRTY_THREE_EACH;
  }

  if (uniqueAccountCount === 2) {
    const allSlots25 =
      partnershipItem.percent1 === 25 &&
      partnershipItem.percent2 === 25 &&
      partnershipItem.percent3 === 25 &&
      partnershipItem.percent4 === 25;

    if (allSlots25 && counts.every((c) => c === 2)) {
      return BillingSplitType.FIFTY_FIFTY;
    }
    if (allSlots25 && counts.some((c) => c === 3)) {
      return BillingSplitType.SEVENTY_FIVE_TWENTY_FIVE;
    }

    // Legacy per-account model: percent values reflect total ownership.
    if (sortedPercentSums[0] === 50 && sortedPercentSums[1] === 50) {
      return BillingSplitType.FIFTY_FIFTY;
    }
    if (sortedPercentSums[0] === 75 && sortedPercentSums[1] === 25) {
      return BillingSplitType.SEVENTY_FIVE_TWENTY_FIVE;
    }

    return null;
  }

  if (uniqueAccountCount === 3) {
    const allSlots25 =
      partnershipItem.percent1 === 25 &&
      partnershipItem.percent2 === 25 &&
      partnershipItem.percent3 === 25 &&
      partnershipItem.percent4 === 25;

    if (allSlots25) return BillingSplitType.FIFTY_TWENTY_FIVE_TWENTY_FIVE;

    // Legacy per-account model: one account sums to 50%, the other two to 25%
    if (
      sortedPercentSums[0] === 50 &&
      sortedPercentSums[1] === 25 &&
      sortedPercentSums[2] === 25
    ) {
      return BillingSplitType.FIFTY_TWENTY_FIVE_TWENTY_FIVE;
    }

    return null;
  }

  if (uniqueAccountCount === 4) {
    if (
      partnershipItem.percent1 === 25 &&
      partnershipItem.percent2 === 25 &&
      partnershipItem.percent3 === 25 &&
      partnershipItem.percent4 === 25
    ) {
      return BillingSplitType.TWENTY_FIVE_EACH;
    }
    return null;
  }

  return null;
};

const UNDO_PARTNERSHIP_CUTOFF_HOUR = 20;

// Partnership/Special Billing can only be undone until 8pm local time the day of OPU.
export const isUndoPartnershipAllowed = (
  opuDate: string | null | undefined,
  now: Date = new Date(),
): boolean => {
  if (!opuDate) return true;

  const parsedOpuDate = parseISO(opuDate);
  if (!isValid(parsedOpuDate)) return true;

  const cutoff = set(parsedOpuDate, {
    hours: UNDO_PARTNERSHIP_CUTOFF_HOUR,
    minutes: 0,
    seconds: 0,
    milliseconds: 0,
  });

  return now <= cutoff;
};
